import JobsService from './jobs-service';
import { JobStatus } from './JobStatus';
import { DraftViewModel } from './models';
import { getDateFilterProps, getTextFilterProps } from './utils';
import {
  modalUtils,
  hasPermissionToRender,
  toSafeArray,
} from '@/app/core/utils';
import { getCurrentFeatureCode } from '@/app/core/auth';
import {
  Fragment,
  Key,
  ReactNode,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { Table } from 'antd';
import {
  DashboardPermissions,
  Draft,
  DraftsSearchResponse,
  Feature,
  JobStatus as JobStatusEnum,
  PaginationConfig,
  ProgramPermissions,
  SearchRequest,
  SortConfig,
  SortDirection,
} from '@ssar-app/domain';
import dayjs from 'dayjs';
import { useHistory, Link } from 'react-router-dom';
import { TablePaginationConfig } from 'antd/lib/table';
import { SorterResult } from 'antd/lib/table/interface';
import { usePathContext } from '@ssar-app/okta-authn-react';
import { Modal, getRandomNumbers } from '@ssar-app/uikit';
import { FaEdit, FaPlusCircle, FaRedo, FaTrash } from 'react-icons/fa/index.js';
import { Button, Flex, HStack, Icon, Text, Tooltip } from '@chakra-ui/react';

interface DraftsProps {
  feature: Feature;
}

export const DraftsList = (props: DraftsProps) => {
  const { buildPath } = usePathContext();
  const [jobs, setJobs] = useState<DraftViewModel[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [pagination, setPagination] = useState<TablePaginationConfig>({
    hideOnSinglePage: false,
    defaultCurrent: 1,
    current: 1,
    total: 0,
  });
  const canCreate = hasPermissionToRender(
    getCurrentFeatureCode(),
    ProgramPermissions.MayCreatePrograms,
  );
  const { feature } = props;
  const history = useHistory();

  const [selectedJobs, setSelectedJobs] = useState<string[]>([]);
  const isAdmin = hasPermissionToRender(
    getCurrentFeatureCode(),
    DashboardPermissions.MayViewAdminColumns,
  );
  const canDeleteDrafts = hasPermissionToRender(
    getCurrentFeatureCode(),
    DashboardPermissions.MayDeleteDrafts,
  );

  const columns = [
    {
      title: 'Status',
      align: 'center' as const,
      sorter: false,
      dataIndex: 'status',
      width: 175,
      render: (_text: string, _row: Draft) => {
        return (
          <JobStatus
            {...{
              status: JobStatusEnum.DRAFTED,
              externalMessage: 'Program Drafted',
            }}
          />
        );
      },
    },
    {
      title: 'Program',
      dataIndex: 'programName',
      sorter: true,
      render: (text: string, record: Draft) => {
        return (
          <Flex alignItems='center'>
            <Tooltip
              placement='top'
              label='Continue editing the draft'
              hasArrow
            >
              <Link
                to={{
                  pathname: buildPath('/program/create'),
                  search: `from=${record.programId}&draftId=${record.id}&productName=${record.system}`,
                  state: { fromDashboard: true },
                }}
              >
                <Icon as={FaEdit} sx={{ mb: '-1px' }} />
              </Link>
            </Tooltip>
            &nbsp;
            {text}
          </Flex>
        );
      },
      ...getTextFilterProps('Program Name'),
    },
    isAdmin
      ? {
          title: 'Subscriber Code',
          align: 'center' as const,
          dataIndex: 'subCode',
          sorter: true,
          key: 'subCode',
          ...getTextFilterProps('Subscriber Code'),
        }
      : null,
    /* isAdmin
      ? {
          title: 'Company ID',
          align: 'center' as const,
          dataIndex: 'clientId',
          sorter: true,
          key: 'clientId',
          ...getTextFilterProps('Company ID'),
        }
      : null, */
    {
      title: 'Created Date',
      dataIndex: 'submittedDate',
      sorter: true,
      ...getDateFilterProps(),
      render: (text: string) => {
        return dayjs(text).format('YYYY-MMM-DD HH:mm:ss [GMT]ZZ');
      },
    },
    {
      title: 'Created By',
      align: 'center' as const,
      dataIndex: 'userId',
      sorter: true,
      ...(isAdmin ? getTextFilterProps('Created By') : {}),
    },
    {
      title: 'Last Updated Date',
      dataIndex: 'lastUpdatedDate',
      sorter: true,
      ...getDateFilterProps(),
      render: (text: string) => {
        return text ? dayjs(text).format('YYYY-MMM-DD HH:mm:ss [GMT]ZZ') : '';
      },
    },
    {
      title: 'Last Updated By',
      align: 'center' as const,
      dataIndex: 'lastUpdatedUserId',
      sorter: true,
      ...getTextFilterProps('Last Updated By'),
    },
  ].filter((a) => a !== null);

  const onSelectChange = (selectedRowKeys: (string | number)[]): void => {
    // console.log('selectedRowKeys changed: ', selectedRowKeys);
    setSelectedJobs(selectedRowKeys.map((a) => a.toString()));
  };

  const rowSelection = {
    selectedRowKeys: selectedJobs.map((a) => a as string | number),
    onChange: onSelectChange,
    preserveSelectedRowKeys: true,
  };

  const fetchData = (
    paginationInput: TablePaginationConfig = pagination,
    filters: Record<string, (Key | boolean)[] | null>,
    sorter: SorterResult<DraftViewModel> | SorterResult<DraftViewModel>[] = [],
  ): void => {
    setIsLoading(true);

    const paginationConfig: PaginationConfig = {
      pageSize: paginationInput?.pageSize || 10,
      current: paginationInput?.current || 1,
    };

    const sortConfig: SortConfig[] = [];

    toSafeArray(sorter).forEach((a) => {
      if (a.field) {
        const dir =
          a.order === 'descend'
            ? SortDirection.Descending
            : a.order === 'ascend'
            ? SortDirection.Ascending
            : null;
        if (dir) {
          sortConfig.push({
            field: a.field.toString(),
            direction: dir,
          });
        }
      }
    });
    // console.log(_filters);
    const searchModel: SearchRequest = {
      paging: paginationConfig,
      sortBy: sortConfig,
      filter: {
        ...filters,
        system: [feature.code],
      },
    };

    JobsService.getDrafts(searchModel)
      .then((jobsResponse: DraftsSearchResponse) => {
        const { paging, data } = jobsResponse;
        const mergedPagination = {
          ...pagination,
          ...paging,
        };
        mergedPagination.current = +mergedPagination.current;
        mergedPagination.pageSize = +mergedPagination.pageSize;
        setPagination(mergedPagination);
        setJobs(
          (data || []).map((a) => ({
            ...a,
            name: a.programName,
          })),
        );
      })
      .catch((err) => {
        console.error(err);
        modalUtils.showError(err, 'Error while getting Drafts');
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const fetchDataWrapper = useCallback(
    () => fetchData(pagination, {}, []),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  useEffect(() => {
    fetchDataWrapper();
  }, [fetchDataWrapper]);

  const showTotal = (total: number, _range: [number, number]): ReactNode => {
    return (
      <div>
        Selected {selectedJobs.length} of {total} draft(s)
      </div>
    );
  };

  function showConfirm(): void {
    Modal.confirm({
      header: 'Are you sure?',
      content: 'Do you want to delete the selected programs?',
      headerProps: { pb: 4 },
      bodyProps: { pt: 0 },
      okButtonProps: { size: 'sm' },
      cancelButtonProps: { size: 'sm' },
      onOk() {
        setIsLoading(true);
        JobsService.deleteDrafts(selectedJobs)
          .then((res) => {
            if (res?.resultCode === 0) {
              modalUtils.showSuccessMessage(res.messages.join('\n'));
              fetchDataWrapper();
            } else {
              modalUtils.showError(res, 'Error while deleting drafts');
            }
          })
          .catch((err) => {
            console.error(err);
            modalUtils.showError(err, 'Error while deleting drafts');
          })
          .finally(() => {
            setIsLoading(false);
            setSelectedJobs([]);
          });
      },
      onCancel() {
        // console.log('Cancel');
      },
    });
  }

  const [key, setKey] = useState(getRandomNumbers(1)[0]);
  // modalUtils.showSuccessMessage('The data was saved successfully.');
  return (
    <div>
      {jobs && (pagination?.total || 1) > 0 ? (
        <Fragment key={key}>
          <Flex justifyContent='space-between' mb={4}>
            <Text className='card-title' fontSize='lg' textColor='black'>
              Drafts Summary
            </Text>
            <HStack>
              {canDeleteDrafts && (
                <Button
                  colorScheme='gray'
                  isDisabled={!(selectedJobs && selectedJobs.length > 0)}
                  onClick={() => showConfirm()}
                  leftIcon={<FaTrash />}
                  variant='outline'
                  size={'sm'}
                >
                  Delete
                </Button>
              )}
              <Button
                colorScheme='gray'
                onClick={() => {
                  setKey(getRandomNumbers(1)[0]);
                  fetchDataWrapper();
                }}
                leftIcon={<FaRedo />}
                variant='outline'
                size={'sm'}
              >
                {'Reset & Refresh'}
              </Button>
              <Button
                variant='solid'
                colorScheme='blue'
                sx={{ boxShadow: 'lg', pr: 4 }}
                isDisabled={!canCreate}
                leftIcon={<FaPlusCircle />}
                onClick={() => history.push(buildPath(`/program/create`))}
              >
                Create
              </Button>
            </HStack>
          </Flex>
          <Table
            onChange={fetchData}
            showSorterTooltip={false}
            className='table-data-list'
            size='small'
            bordered={true}
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            rowKey={(row) => row.id || (row as any)['_id']}
            loading={isLoading}
            rowSelection={canDeleteDrafts ? rowSelection : undefined}
            dataSource={jobs}
            columns={columns}
            pagination={{
              ...pagination,
              showTotal,
              position: ['bottomLeft'],
              showSizeChanger: true,
              simple: false,
            }}
          >
            {/* <Column dataIndex="status"></Column> */}
          </Table>
        </Fragment>
      ) : (
        <div>No Permission.</div>
      )}
    </div>
  );
};
